import { Dispatch, SetStateAction } from 'react'
import dayjs from 'dayjs'
import { LocalStorageKeyEnum } from 'enums/common'
import { PusherEventName } from 'enums/pusher'
import { toast } from 'react-toastify'
import { CommonError } from 'types'
import { createDriverLicenseVerification as createDriverLicenseVerificationWithUserId } from 'API/applicant'
import { handleError } from 'API/error'
import { createDriverLicenseVerification, getDriverLicenseVerification } from 'API/houseApplication'
import { getSigningLeaseAgreement } from 'API/listing'
import { getStripeClientSecret } from 'API/stripe'
import { IApplicationFormValues, ILeaseAgreementPayload, ISigningEnvelopePayload } from 'interfaces/application'
import { IStripeClient } from 'interfaces/stripe'
import { IUnit } from 'interfaces/unit'
import { convertUSDToCent, getFullName, getRawUSPhoneNumber } from 'utils/common'
import { pusher } from 'utils/pusher'
import { ECompleteScreeningSteps } from '../components/CompleteScreening/utils'
import {
  DOCUSIGN_LOADING_PAGE_URL,
  EDriverLicenseTypeEnum,
  IBerbixTransactionResponse,
  SECURITY_DEPOSIT
} from '../constant'
import { getDriverLicenseChannel } from '.'

export async function fetchClientSecret(
  setClientSecret: Dispatch<SetStateAction<string | null>>,
  setClientInfo: Dispatch<SetStateAction<IStripeClient | undefined>>,
  amount: number,
  formValue?: IApplicationFormValues,
  currentClientInfo?: IStripeClient
): Promise<void> {
  try {
    const clientInfo: IStripeClient = {
      name: getFullName(formValue?.firstName, formValue?.lastName),
      email: formValue?.email,
      phone: getRawUSPhoneNumber(formValue?.phoneNumber!),
      amount: convertUSDToCent(amount)
    }
    if (
      currentClientInfo &&
      currentClientInfo?.email === clientInfo.email &&
      currentClientInfo?.name === clientInfo.name
    ) {
      return
    }

    const secret: string = await getStripeClientSecret(clientInfo)
    if (secret) {
      setClientSecret(secret || null)
      setClientInfo(clientInfo)
    }
  } catch (error) {
    handleError(
      error as CommonError,
      'components/pages/DetailPage/components/ApplicationFormModalV2',
      'fetchClientSecret'
    )
  }
}

export async function fetchAgreementSigningUrl(
  formValue: IApplicationFormValues,
  shortAddress: string,
  buildiumPropertyId?: number,
  unit?: IUnit,
  callback?: Function
): Promise<void> {
  try {
    const membershipStartDate = formValue?.moveInDate ?? new Date()
    const agreementData: ILeaseAgreementPayload = {
      homeAddress: `${formValue?.address ?? ''} ${unit?.name} ${buildiumPropertyId ?? ''}`,
      membershipStartDate,
      membershipEndDate: dayjs(membershipStartDate).add(1, 'year').toDate(),
      securityDeposit: SECURITY_DEPOSIT,
      phoneNumber: formValue?.phoneNumber ?? '',
      shortAddress: `${shortAddress ?? ''} ${unit?.name}`,
      fullName: `${formValue?.firstName ?? ''} ${formValue?.lastName ?? ''}`,
      email: formValue?.email ?? '',
      price: unit?.price ?? 0,
      returnUrl: DOCUSIGN_LOADING_PAGE_URL
    }
    const envelopeData: ISigningEnvelopePayload = await getSigningLeaseAgreement(agreementData)

    if (typeof callback === 'function') {
      callback(envelopeData)
    }
  } catch (error) {
    handleError(
      error as CommonError,
      'components/pages/DetailPage/components/ApplicationFormModalV2',
      'fetchAgreementSigningUrl'
    )
  }
}

export async function getDriverLicenseVerificationUrl(
  phoneNumber: number | string,
  setUrl: Dispatch<SetStateAction<string>>,
  isEnableLoginFeature?: boolean,
  userId?: string
): Promise<void> {
  try {
    const customerUid: string = String(phoneNumber)
    const response: IBerbixTransactionResponse = await createDriverLicenseVerification(customerUid)
    if (isEnableLoginFeature) {
      const responseWithUserId: IBerbixTransactionResponse = await createDriverLicenseVerificationWithUserId(
        String(userId)
      )
      if (response?.hostedUrl && responseWithUserId?.hostedUrl) {
        localStorage.setItem(LocalStorageKeyEnum.REFRESH_TOKEN, response?.refreshToken)
        setUrl(response?.hostedUrl)
      } else {
        localStorage.setItem(LocalStorageKeyEnum.REFRESH_TOKEN, '')
      }
    } else {
      if (response?.hostedUrl) {
        localStorage.setItem(LocalStorageKeyEnum.REFRESH_TOKEN, response?.refreshToken)
        setUrl(response?.hostedUrl)
      } else {
        localStorage.setItem(LocalStorageKeyEnum.REFRESH_TOKEN, '')
      }
    }
  } catch (error) {
    handleError(
      error as CommonError,
      'components/pages/DetailPage/components/ApplicationFormModalV2/utils.tsx',
      'getDriverLicenseVerificationUrl'
    )
  }
}

export async function subscribeDriverLicenseHook(
  buildiumUnitId: number,
  phoneNumber: string,
  isDriverLicenseVerified: boolean,
  setUrl: Dispatch<SetStateAction<string>>,
  setStep: (step: number) => void,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  setIsDriverLicenseVerified: Dispatch<SetStateAction<boolean>>,
  buildiumPropertyId?: number
): Promise<void> {
  isDriverLicenseVerified === false && setIsLoading(true)
  const driverLicenseChannel: string = getDriverLicenseChannel(phoneNumber)
  const verifyDriverLicenseChannel = pusher.subscribe(driverLicenseChannel)

  verifyDriverLicenseChannel.bind(
    PusherEventName.REFETCH,
    async function (payload: { action: EDriverLicenseTypeEnum }) {
      try {
        if (payload?.action === EDriverLicenseTypeEnum.ACCEPT || payload?.action === EDriverLicenseTypeEnum.REVIEW) {
          const refreshToken: string = localStorage.getItem(LocalStorageKeyEnum.REFRESH_TOKEN) ?? ''
          getDriverLicenseVerification(refreshToken, buildiumPropertyId ?? 0, buildiumUnitId)
          setIsLoading(false)
          setIsDriverLicenseVerified(true)
          setStep(ECompleteScreeningSteps.PAYSTUB_UPLOADER)
        }
        if (payload?.action === EDriverLicenseTypeEnum.REJECT) {
          await getDriverLicenseVerificationUrl(phoneNumber, setUrl)
          toast.warning('Your driver license was rejected, please reverify your driver license!')
        }
      } catch (error) {
        handleError(
          error as CommonError,
          'components/pages/DetailPage/components/ApplicationFormModalV2/utils.tsx',
          'subscribeDriverLicenseHook'
        )
      } finally {
        pusher.unsubscribe(driverLicenseChannel)
      }
    }
  )
}
